import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Tabs, 
  Tab, 
  CircularProgress,
  Button,
  Chip,
  LinearProgress,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  User, 
  Building2, 
  BarChart4, 
  Brain, 
  Mail,
  Share2,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import { Prospect, BuyerPersona, EngagementMetrics, EmailTemplate } from '../../types/savvydata.ts';
import api from '../../services/savvydata/api.ts';

const ContentContainer = styled(Paper)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const MetricBox = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  '& .MuiTabs-indicator': {
    backgroundColor: '#00FFFF',
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  '&.Mui-selected': {
    color: '#00FFFF',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(45deg, #9370DB 30%, #00FFD1 90%)',
    boxShadow: '0 0 10px rgba(138, 43, 226, 0.5)',
  },
}));

const PersonalityChart = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const ScoreBar = styled(Box)<{ value: number }>(({ theme, value }) => ({
  position: 'relative',
  height: 8,
  borderRadius: 4,
  backgroundColor: 'rgba(26, 26, 46, 0.8)',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${value * 100}%`,
    borderRadius: 4,
    background: `linear-gradient(to right, #00CC99, #8A2BE2)`,
  },
}));

const EmailPreview = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.8)',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  '& .subject': {
    fontWeight: 'bold',
    color: '#00FFFF',
    marginBottom: theme.spacing(1),
  },
  '& .body': {
    whiteSpace: 'pre-wrap',
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const EditableEmailField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    color: '#fff',
    backgroundColor: 'rgba(26, 26, 46, 0.8)',
    fontFamily: 'inherit',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.3)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(138, 43, 226, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#8A2BE2',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255, 255, 255, 0.7)',
  },
}));

interface ProspectAnalyticsProps {
  prospect: Prospect;
  onGenerateProposal: () => void;
  hasProposal?: boolean;
}

const ProspectAnalytics: React.FC<ProspectAnalyticsProps> = ({ prospect, onGenerateProposal, hasProposal = false }) => {
  const [tabValue, setTabValue] = useState(0);
  const [persona, setPersona] = useState<BuyerPersona | null>(null);
  const [engagement, setEngagement] = useState<EngagementMetrics | null>(null);
  const [emailTemplate, setEmailTemplate] = useState<EmailTemplate | null>(null);
  const [loadingPersona, setLoadingPersona] = useState(false);
  const [loadingEngagement, setLoadingEngagement] = useState(false);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmailSubject, setEditedEmailSubject] = useState('');
  const [editedEmailBody, setEditedEmailBody] = useState('');
  
  useEffect(() => {
    if (prospect) {
      loadPersonaData();
    }
  }, [prospect]);
  
  useEffect(() => {
    if (emailTemplate) {
      setEditedEmailSubject(emailTemplate.subject);
      setEditedEmailBody(emailTemplate.body);
    }
  }, [emailTemplate]);
  
  const loadPersonaData = async () => {
    setLoadingPersona(true);
    try {
      const personaData = await api.analytics.getPersonaAnalysis(prospect.id);
      setPersona(personaData);
    } catch (error) {
      console.error('Error loading persona data:', error);
    } finally {
      setLoadingPersona(false);
    }
  };
  
  const loadEngagementData = async () => {
    setLoadingEngagement(true);
    try {
      const engagementData = await api.analytics.getEngagementMetrics(prospect.id);
      setEngagement(engagementData);
    } catch (error) {
      console.error('Error loading engagement data:', error);
    } finally {
      setLoadingEngagement(false);
    }
  };
  
  const generateEmail = async () => {
    setLoadingEmail(true);
    try {
      const email = await api.email.generateEmail(prospect.id, 'initial_outreach');
      setEmailTemplate(email);
    } catch (error) {
      console.error('Error generating email:', error);
    } finally {
      setLoadingEmail(false);
    }
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    if (newValue === 1 && !engagement) {
      loadEngagementData();
    } else if (newValue === 3 && !emailTemplate) {
      generateEmail();
    }
  };
  
  const handleStartEditingEmail = () => {
    setIsEditingEmail(true);
  };
  
  const handleSaveEmail = () => {
    if (emailTemplate) {
      emailTemplate.subject = editedEmailSubject;
      emailTemplate.body = editedEmailBody;
    }
    setIsEditingEmail(false);
  };
  
  const handleCancelEditingEmail = () => {
    if (emailTemplate) {
      setEditedEmailSubject(emailTemplate.subject);
      setEditedEmailBody(emailTemplate.body);
    }
    setIsEditingEmail(false);
  };
  
  const handleSendEmail = () => {
    alert('Email sent successfully!');
  };
  
  const renderProspectInfo = () => (
    <>
      <Typography variant="h6" sx={{ color: '#00ffff', mb: 2 }}>
        Prospect Information
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
        <Box sx={{ mr: 2 }}>
          <User size={48} color="#8A2BE2" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            {prospect.name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            {prospect.location}
          </Typography>
        </Box>
      </Box>
      
      <MetricBox>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Building2 size={20} color="#00CC99" style={{ marginRight: 8 }} />
          <Typography variant="subtitle2" sx={{ color: '#00CC99' }}>
            Company Details
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#fff', mb: 1 }}>
          {prospect.company_name}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip 
            label={prospect.industry} 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(138, 43, 226, 0.2)',
              color: '#fff',
              borderRadius: '4px'
            }} 
          />
          {prospect.employee_count && (
            <Chip 
              label={`${prospect.employee_count} Employees`} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(0, 204, 153, 0.2)',
                color: '#fff',
                borderRadius: '4px'
              }} 
            />
          )}
          {prospect.revenue_range && (
            <Chip 
              label={prospect.revenue_range} 
              size="small" 
              sx={{ 
                backgroundColor: 'rgba(0, 255, 255, 0.2)',
                color: '#fff',
                borderRadius: '4px'
              }} 
            />
          )}
        </Box>
      </MetricBox>
      
      {prospect.score && (
        <MetricBox>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <BarChart4 size={20} color="#00FFFF" style={{ marginRight: 8 }} />
            <Typography variant="subtitle2" sx={{ color: '#00FFFF' }}>
              Prospect Scoring
            </Typography>
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Score
              </Typography>
              <Typography variant="body2" sx={{ color: '#00FFFF' }}>
                {(prospect.score.total_score * 100).toFixed(0)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={prospect.score.total_score * 100} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                backgroundColor: 'rgba(26, 26, 46, 0.4)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#00FFFF',
                  borderRadius: 4,
                }
              }} 
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Relevance
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={prospect.score.relevance * 100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#8A2BE2',
                    borderRadius: 3,
                  }
                }} 
              />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Engagement Potential
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={prospect.score.engagement_potential * 100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#00CC99',
                    borderRadius: 3,
                  }
                }} 
              />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Budget Fit
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={prospect.score.budget_fit * 100} 
                sx={{ 
                  height: 6, 
                  borderRadius: 3,
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF6F00',
                    borderRadius: 3,
                  }
                }} 
              />
            </Box>
          </Box>
        </MetricBox>
      )}
      
      {prospect.technologies && prospect.technologies.length > 0 && (
        <MetricBox>
          <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
            Technology Stack
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {prospect.technologies.map((tech, index) => (
              <Chip 
                key={index}
                label={tech} 
                size="small" 
                sx={{ 
                  backgroundColor: 'rgba(0, 204, 153, 0.2)',
                  color: '#fff',
                  borderRadius: '4px'
                }} 
              />
            ))}
          </Box>
        </MetricBox>
      )}
      
      <ActionButton 
        fullWidth 
        onClick={onGenerateProposal}
        startIcon={<Share2 />}
      >
        Generate Proposal
      </ActionButton>
    </>
  );
  
  const renderPersonaAnalysis = () => (
    <>
      {loadingPersona ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : persona ? (
        <>
          <Typography variant="h6" sx={{ color: '#00ffff', mb: 2 }}>
            Buyer Persona Analysis
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ mr: 2 }}>
              <Brain size={48} color="#8A2BE2" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {persona.adoption_stage} Adopter
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {persona.budget_authority} Budget Authority
              </Typography>
            </Box>
          </Box>
          
          <MetricBox>
            <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
              Personality Profile
            </Typography>
            
            <PersonalityChart>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Openness
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.personality.openness * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.personality.openness} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Conscientiousness
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.personality.conscientiousness * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.personality.conscientiousness} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Extraversion
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.personality.extraversion * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.personality.extraversion} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Agreeableness
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.personality.agreeableness * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.personality.agreeableness} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Neuroticism
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.personality.neuroticism * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.personality.neuroticism} />
              </Box>
            </PersonalityChart>
          </MetricBox>
          
          <MetricBox>
            <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
              Decision Making Style
            </Typography>
            
            <PersonalityChart>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Analytical
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.decision_style.analytical * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.decision_style.analytical} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Intuitive
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.decision_style.intuitive * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.decision_style.intuitive} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Collaborative
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.decision_style.collaborative * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.decision_style.collaborative} />
              </Box>
              
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    Directive
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#fff' }}>
                    {(persona.decision_style.directive * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <ScoreBar value={persona.decision_style.directive} />
              </Box>
            </PersonalityChart>
          </MetricBox>
          
          <MetricBox>
            <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
              Key Insights
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                Communication Preferences:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {persona.communication_preferences.map((pref, index) => (
                  <Chip 
                    key={index}
                    label={pref} 
                    size="small" 
                    sx={{ 
                      backgroundColor: 'rgba(138, 43, 226, 0.2)',
                      color: '#fff',
                      borderRadius: '4px'
                    }} 
                  />
                ))}
              </Box>
              
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
            Pain Points:
            </Typography>
            {persona.pain_points.map((point, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ color: '#fff' }}>
                    {point.text}
                </Typography>
                </Box>
                <Box sx={{ width: 100 }}>
                <LinearProgress 
                    variant="determinate" 
                    value={point.intensity * 100} 
                    sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: 'rgba(26, 26, 46, 0.4)',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: point.intensity > 0.7 ? '#FF4500' : '#00CC99',
                        borderRadius: 3,
                    }
                    }} 
                />
                </Box>
            </Box>
            ))}
        </Box>
        </MetricBox>
    </>
    ) : (
    <Typography variant="body1" color="text.secondary">
        No persona data available
    </Typography>
    )}
</>
);

const renderEngagement = () => (
  <>
    {loadingEngagement ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    ) : engagement ? (
      <>
        <Typography variant="h6" sx={{ color: '#00ffff', mb: 2 }}>
          Engagement Analysis
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ mr: 2 }}>
            <TrendingUp size={48} color="#00CC99" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {engagement.overall_score > 0.7 ? 'High' : engagement.overall_score > 0.4 ? 'Medium' : 'Low'} Engagement
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Velocity: {engagement.velocity > 0 ? 'Increasing' : 'Decreasing'}
            </Typography>
          </Box>
        </Box>
        
        <MetricBox>
          <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
            Engagement Metrics
          </Typography>
          
          <PersonalityChart>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Email Engagement
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>
                  {(engagement.email_engagement * 100).toFixed(0)}%
                </Typography>
              </Box>
              <ScoreBar value={engagement.email_engagement} />
            </Box>
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Content Engagement
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>
                  {(engagement.content_engagement * 100).toFixed(0)}%
                </Typography>
              </Box>
              <ScoreBar value={engagement.content_engagement} />
            </Box>
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Meeting Engagement
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>
                  {(engagement.meeting_engagement * 100).toFixed(0)}%
                </Typography>
              </Box>
              <ScoreBar value={engagement.meeting_engagement} />
            </Box>
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  CRM Activity
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>
                  {(engagement.crm_activity * 100).toFixed(0)}%
                </Typography>
              </Box>
              <ScoreBar value={engagement.crm_activity} />
            </Box>
            
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Social Engagement
                </Typography>
                <Typography variant="caption" sx={{ color: '#fff' }}>
                  {(engagement.social_engagement * 100).toFixed(0)}%
                </Typography>
              </Box>
              <ScoreBar value={engagement.social_engagement} />
            </Box>
          </PersonalityChart>
        </MetricBox>
        
        <MetricBox>
          <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
            Engagement Patterns
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 2 }}>
            <Box sx={{ flex: '1 0 45%', minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                Frequency
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: '#00CC99', mr: 1 }}>
                  {(engagement.frequency * 10).toFixed(1)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  interactions/month
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: '1 0 45%', minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                Recency
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: '#00CC99', mr: 1 }}>
                  {(engagement.recency * 30).toFixed(0)}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  days ago
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: '1 0 45%', minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                Depth
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={engagement.depth * 100} 
                sx={{ 
                  height: 10, 
                  borderRadius: 5,
                  backgroundColor: 'rgba(26, 26, 46, 0.4)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(to right, #00CC99, #8A2BE2)',
                    borderRadius: 5,
                  }
                }} 
              />
            </Box>
            
            <Box sx={{ flex: '1 0 45%', minWidth: 120 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                Velocity
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h4" sx={{ color: engagement.velocity > 0 ? '#00CC99' : '#FF4500', mr: 1 }}>
                  {(engagement.velocity * 100).toFixed(0)}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {engagement.velocity > 0 ? 'growth' : 'decline'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </MetricBox>
      </>
    ) : (
      <Typography variant="body1" color="text.secondary">
        No engagement data available
      </Typography>
    )}
  </>
);

const renderEmailTemplate = () => {
  if (loadingEmail) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (!emailTemplate) {
    return (
      <Typography variant="body1" color="text.secondary">
        No email template available
      </Typography>
    );
  }
  
  return (
    <>
      <Typography variant="h6" sx={{ color: '#00ffff', mb: 2 }}>
        AI-Generated Email Template
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ mr: 2 }}>
          <Mail size={48} color="#8A2BE2" />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Initial Outreach Email
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Personalized for {prospect.name}
          </Typography>
        </Box>
        {!isEditingEmail && (
          <Button 
            variant="outlined" 
            size="small" 
            sx={{ 
              ml: 'auto', 
              borderColor: 'rgba(0, 255, 255, 0.5)', 
              color: '#00FFFF',
              '&:hover': {
                borderColor: '#00FFFF',
                background: 'rgba(0, 255, 255, 0.1)',
              }
            }}
            onClick={handleStartEditingEmail}
          >
            Edit Email
          </Button>
        )}
      </Box>
      
      {isEditingEmail ? (
        <Box sx={{ mb: 3 }}>
          <EditableEmailField
            label="Subject"
            fullWidth
            value={editedEmailSubject}
            onChange={(e) => setEditedEmailSubject(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
          <EditableEmailField
            label="Email Body"
            fullWidth
            value={editedEmailBody}
            onChange={(e) => setEditedEmailBody(e.target.value)}
            variant="outlined"
            multiline
            rows={10}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={handleCancelEditingEmail}
              sx={{ 
                borderColor: 'rgba(138, 43, 226, 0.5)',
                color: '#fff',
                '&:hover': {
                  borderColor: 'rgba(138, 43, 226, 0.8)',
                  background: 'rgba(138, 43, 226, 0.1)',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              size="small"
              onClick={handleSaveEmail}
              sx={{ 
                background: 'linear-gradient(45deg, #8A2BE2 30%, #00CC99 90%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(45deg, #9370DB 30%, #00FFD1 90%)',
                }
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      ) : (
        <EmailPreview>
          <Typography className="subject" variant="subtitle1">
            {emailTemplate.subject}
          </Typography>
          <Divider sx={{ my: 1, borderColor: 'rgba(138, 43, 226, 0.2)' }} />
          <Typography className="body" variant="body2">
            {emailTemplate.body}
            
            {hasProposal && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 255, 255, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#00FFFF' }}>
                  View Your Proposal: 
                </Typography>
                <Typography variant="body2" component="span">
                  https://modelistic.ai/savvydata.ai/proposals/solution-baguley
                </Typography>
              </Box>
            )}
          </Typography>
        </EmailPreview>
      )}
      
      <MetricBox sx={{ mt: 3 }}>
        <Typography variant="subtitle2" sx={{ color: '#00FFFF', mb: 1 }}>
          Personalization Elements
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
            Pain Points Addressed:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {emailTemplate.personalization.pain_points_addressed.map((point, index) => (
              <Chip 
                key={index}
                label={point} 
                size="small" 
                icon={<CheckCircle size={14} />}
                sx={{ 
                  backgroundColor: 'rgba(0, 204, 153, 0.2)',
                  color: '#fff',
                  borderRadius: '4px'
                }} 
              />
            ))}
          </Box>
        </Box>
      </MetricBox>
      
      <Box sx={{ display: 'flex', mt: 3 }}>
        <ActionButton
          startIcon={<Mail />}
          sx={{ flex: 1, mr: 1 }}
          onClick={handleSendEmail}
        >
          Send Email
        </ActionButton>
        <ActionButton
          startIcon={<Share2 />}
          sx={{ flex: 1, ml: 1 }}
          onClick={onGenerateProposal}
        >
          {hasProposal ? 'Edit Proposal' : 'Generate Proposal'}
        </ActionButton>
      </Box>
    </>
  );
};

return (
  <ContentContainer>
    <StyledTabs
      value={tabValue}
      onChange={handleTabChange}
      variant="fullWidth"
    >
      <StyledTab label="Prospect Info" />
      <StyledTab label="Buyer Persona" />
      <StyledTab label="Engagement" />
      <StyledTab label="Email Template" />
    </StyledTabs>
    
    {tabValue === 0 && renderProspectInfo()}
    {tabValue === 1 && renderPersonaAnalysis()}
    {tabValue === 2 && renderEngagement()}
    {tabValue === 3 && renderEmailTemplate()}
  </ContentContainer>
);
};

export default ProspectAnalytics;